'use client';

import { c as _c } from "react/compiler-runtime";
import { createContext, useContext, useState, useCallback, useRef } from 'react';
import ConfirmDialog from '@/app/_components/ConfirmDialog';

/**
 * ConfirmDialogContext
 *
 * Provides a system-level confirmation dialog that can be triggered from anywhere
 * in the app via the useConfirmDialog hook.
 *
 * The confirm() function returns a promise that resolves to:
 * - The action string of the clicked button (e.g., 'remove', 'save')
 * - null if the user cancelled (X button, background click, or Cancel link)
 *
 * Example usage:
 *
 * const { confirm } = useConfirmDialog()
 *
 * const handleRemove = async () => {
 *     const result = await confirm({
 *         title: 'Remove Item?',
 *         message: 'What would you like to do with this item?',
 *         buttons: [
 *             { label: 'Remove from Cart', action: 'remove', variant: 'primary' },
 *             { label: 'Save for Later', action: 'save', variant: 'secondary' }
 *         ]
 *     })
 *
 *     if (result === 'remove') {
 *         // User clicked "Remove from Cart"
 *         deleteItem()
 *     } else if (result === 'save') {
 *         // User clicked "Save for Later"
 *         saveForLater()
 *     }
 *     // result === null means cancelled
 * }
 */

const ConfirmDialogContext = createContext(null);
export const ConfirmDialogProvider = t0 => {
  const $ = _c(9);
  const {
    children
  } = t0;
  const [isOpen, setIsOpen] = useState(false);
  const [config, setConfig] = useState(null);
  const resolveRef = useRef(null);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = dialogConfig => new Promise(resolve => {
      resolveRef.current = resolve;
      setConfig(dialogConfig);
      setIsOpen(true);
    });
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const confirm = t1;
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = result => {
      setIsOpen(false);
      setTimeout(() => {
        setConfig(null);
      }, 250);
      if (resolveRef.current) {
        resolveRef.current(result);
        resolveRef.current = null;
      }
    };
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const handleClose = t2;
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      confirm
    };
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  let t4;
  if ($[3] !== config || $[4] !== isOpen) {
    t4 = <ConfirmDialog isOpen={isOpen} onClose={handleClose} config={config} />;
    $[3] = config;
    $[4] = isOpen;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== children || $[7] !== t4) {
    t5 = <ConfirmDialogContext.Provider value={t3}>{children}{t4}</ConfirmDialogContext.Provider>;
    $[6] = children;
    $[7] = t4;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  return t5;
};
export const useConfirmDialog = () => {
  const context = useContext(ConfirmDialogContext);
  if (!context) {
    throw new Error("useConfirmDialog must be used within a ConfirmDialogProvider");
  }
  return context;
};